import {
  computed,
  hasInjectionContext,
  inject,
  provide,
  reactive,
  readonly,
  toValue,
  watchEffect,
  type ComputedRef,
  type DeepReadonly,
  type InjectionKey,
  type MaybeRefOrGetter,
} from 'vue';
import {
  axisAttr,
  designAxes,
  isValidAxisValue,
  type DesignAxisName,
  type DesignState,
} from './design-axes';

function initialState(): DesignState {
  const s = {} as DesignState;
  for (const name of Object.keys(designAxes) as DesignAxisName[]) {
    // `as never` is the idiomatic TS escape for correlated-union index access
    // (s[name] collapses to `never` once more than one axis exists).
    s[name] = designAxes[name].default as never;
  }
  return s;
}

/** Global source of truth — one value per axis. */
const state = reactive<DesignState>(initialState());

/** Ad-hoc, app-specific keys (escape hatch). null ⇒ attribute removed. */
const rawState = reactive<Record<string, string | null>>({});

/** The axis values a subtree sets for itself (`provideDesignScope`). */
const designScopeKey: InjectionKey<ComputedRef<Partial<DesignState>>> = Symbol('nx-design-scope');

/** The global store with a scope's values on top, read axis by axis (stays reactive to both). */
function scopedState(scope: ComputedRef<Partial<DesignState>>): DeepReadonly<DesignState> {
  const view = {} as DesignState;
  for (const name of Object.keys(designAxes) as DesignAxisName[]) {
    Object.defineProperty(view, name, {
      enumerable: true,
      get: () => scope.value[name] ?? state[name],
    });
  }
  return readonly(view);
}

/** Reactive read of the current design axes. Most components never need this —
 *  the CSS cascade restyles them. Use it only to render differently, or to
 *  drive a settings toggle. Inside a `provideDesignScope` subtree it reads the
 *  scope's values over the global store. */
export function useDesign(): DeepReadonly<DesignState> {
  const scope = hasInjectionContext() ? inject(designScopeKey, null) : null;
  return scope ? scopedState(scope) : readonly(state);
}

/**
 * Gives the calling component's subtree axis values of its own — a preview
 * that shows other settings than the shell, say. `useDesign()` below it reads
 * them over the global store (an axis the scope leaves out follows the store;
 * an invalid value is ignored), so the axes a component READS (`reveal`,
 * `savestatus` in NxInputWrapper) follow the scope too. The cascade-only axes
 * need the matching `data-nx-<axis>` attributes on an element of the subtree,
 * which the caller renders. A scope inside a scope adds its values on top.
 */
export function provideDesignScope(values: MaybeRefOrGetter<Partial<DesignState>>): void {
  const outer = inject(designScopeKey, null);
  const scope = computed<Partial<DesignState>>(() => {
    const own: Partial<Record<DesignAxisName, string>> = {};
    const raw: Partial<Record<string, unknown>> = toValue(values) ?? {};
    for (const name of Object.keys(designAxes) as DesignAxisName[]) {
      const value = raw[name];
      if (typeof value === 'string' && isValidAxisValue(name, value)) own[name] = value;
    }
    return { ...outer?.value, ...(own as Partial<DesignState>) };
  });
  provide(designScopeKey, scope);
}

/** Type-safe setter for a known axis. An out-of-range value is a compile error;
 *  at runtime an unknown axis/value is ignored. */
export function setDesign<K extends DesignAxisName>(axis: K, value: DesignState[K]): void {
  if (!isValidAxisValue(axis as string, value as string)) return;
  state[axis] = value;
}

/** Escape hatch for app-specific ad-hoc keys → data-nx-<key>. null removes it. */
export function setDesignRaw(key: string, value: string | null): void {
  rawState[key] = value;
}

/** Minimal element shape we write to — keeps this testable in a node env and
 *  free of a hard DOM dependency. */
export type DesignRootEl = Pick<Element, 'setAttribute' | 'removeAttribute'>;

/**
 * Bind the design state to a root element: writes `data-nx-<axis>` for every
 * axis plus `data-nx-<key>` for raw keys, and keeps them in sync as the store
 * changes. Call once from the app shell (typically on document.documentElement).
 * Returns a stop() handle that disposes the binding.
 *
 * A subtree with axes of its own: put `data-nx-<axis>` on a subtree element and
 * the CSS cascade makes the inner value win; `provideDesignScope` gives the
 * axes a component reads in JS the same values. Content the subtree teleports
 * elsewhere (a dialog on `<body>`) leaves its DOM: the attributes no longer
 * reach it, only this global root value does.
 */
export function applyDesignToRoot(el: DesignRootEl = document.documentElement): () => void {
  return watchEffect(() => {
    for (const name of Object.keys(designAxes) as DesignAxisName[]) {
      el.setAttribute(axisAttr(name), state[name]);
    }
    for (const [key, value] of Object.entries(rawState)) {
      if (value == null) el.removeAttribute(axisAttr(key));
      else el.setAttribute(axisAttr(key), value);
    }
  });
}
